import { ProductsAdminState, UpdatedData } from "./types";
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "store/rootReducer";
import { API_URL } from "config";
import userStorage from "store/UserStorage";
import { batch } from "react-redux";
import { setResponseMessage } from "../adminSlice";
import { ObjectType } from "features/types";

const initialState: ProductsAdminState = {
  products: [],
  product: undefined,
  options: [],
  loading: false,
};

const getRequestErrorMessage = (error: any, fallback: string) => {
  const responseData = error && error.response && error.response.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (responseData && typeof responseData.message === "string") {
    return responseData.message;
  }

  return (error && error.message) || fallback;
};

const productsAdmin = createSlice({
  name: "productsAdminSlice",
  initialState,
  reducers: {
    setProducts(state, action) {
      state.products = action.payload;
    },
    setProductForEdit(state, action) {
      state.product = action.payload;
      state.loading = false;
    },
    setOptions(state, action) {
      state.options = action.payload;
      state.loading = false;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
  },
});
export const { setProducts, setLoading, setOptions, setProductForEdit } = productsAdmin.actions;
export const selectProductsIds = (state: RootState) => state.productsAdmin.products;
export const selectLoading = (state: RootState) => state.productsAdmin.loading;
export const selectProductsOptions = (state: RootState) => state.productsAdmin.options;
export const selectProductForEditing = (state: RootState) => state.productsAdmin.product;
export const fetchProductsList = (type: string = "products-all") => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    const result = await axios.get(`${API_URL}/admin/${type === "product" ? "products" : type}`);
    batch(() => {
      dispatch(setProducts(result.data.data));
      dispatch(setLoading(false));
    });
  } catch (error: any) {
    batch(() => {
      dispatch(setResponseMessage({ message: error.message, hasError: true }));
      dispatch(setLoading(false));
    });
    console.log(`Error in fetchProductsList: ${error.message}`);
  }
};

export const fetchProductsOptions = (type: string = "product") => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    const result = await axios.get(`${API_URL}/admin/${type}-options`);
    batch(() => {
      dispatch(setOptions(result.data.data));
      dispatch(setResponseMessage({ message: "", hasError: false }));
    });
  } catch (error: any) {
    batch(() => {
      dispatch(setResponseMessage({ message: error.message, hasError: true }));
      dispatch(setLoading(false));
    });
    console.log(`Error in fetchProductsOptions: ${error.message}`);
  }
};
export const addProduct = (data: any, onUploadProgress: (e: ProgressEvent<EventTarget>) => void) => async (
  dispatch: any
) => {
  try {
    dispatch(setLoading(true));
    let formData = new FormData();
    formData.append("product", JSON.stringify({ ...data.fields, objectType: ObjectType.product }));

    for (var x = 0; x < data.images.length; x++) {
      formData.append("file", data.images[x]);
    }
    for (var x = 0; x < data.extrasImages.length; x++) {
      formData.append("extrasImages", data.extrasImages[x]);
    }
    let token = userStorage.getUser();
    const response = await axios.post(`${API_URL}/admin/products`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно качихте продукта.",
          hasError: false,
        })
      );
      dispatch(fetchProductsList("product"));
    });
    return true;
  } catch (error: any) {
    batch(() => {
      dispatch(
        setResponseMessage({
          message: getRequestErrorMessage(
            error,
            "Продуктът не можа да бъде създаден."
          ),
          hasError: true,
        })
      );
      dispatch(setLoading(false));
    });

    return false;
  }
};
export const addMultisplit = (data: any, onUploadProgress: (e: ProgressEvent<EventTarget>) => void) => async (
  dispatch: any
) => {
  try {
    dispatch(setLoading(true));
    let formData = new FormData();
    formData.append("product", JSON.stringify({ ...data.fields, objectType: ObjectType.product }));

    for (var x = 0; x < data.images.length; x++) {
      formData.append("file", data.images[x]);
    }
    for (var x = 0; x < data.internal_bodies_images.length; x++) {
      formData.append("internal_bodies_images", data.internal_bodies_images[x]);
    }
    let token = userStorage.getUser();
    const response = await axios.post(`${API_URL}/admin/multisplit`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно качихте продукта.",
          hasError: false,
        })
      );
      dispatch(fetchProductsList("multisplit"));
    });
    return true;
  } catch (error: any) {
    batch(() => {
      dispatch(
        setResponseMessage({
          message: getRequestErrorMessage(
            error,
            "Мултисплит системата не можа да бъде създадена."
          ),
          hasError: true,
        })
      );
      dispatch(setLoading(false));
    });

    return false;
  }
};
export const updateProduct = (data: UpdatedData, onUploadProgress: (e: ProgressEvent<EventTarget>) => void) => async (
  dispatch: any
) => {
  try {
    dispatch(setLoading(true));
    let formData = new FormData();
    formData.append("product", JSON.stringify(data.data.fields));
    for (var x = 0; x < data.data.images.length; x++) {
      formData.append("file", data.data.images[x]);
    }
    for (var x = 0; x < data.data.extrasImages.length; x++) {
      formData.append("extrasImages", data.data.extrasImages[x]);
    }
    let token = userStorage.getUser();
    const response = await axios.put(`${API_URL}/admin/products/${data.id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно променихте продукта.",
          hasError: false,
        })
      );
      dispatch(fetchProductsList("product"));
    });
  } catch (error: any) {
    dispatch(setResponseMessage({ message: error.message, hasError: true }));

    //  console.log(`Error in addProduct: ${error.message}`);
  }
};

export const updateMultisplit = (
  data: UpdatedData,
  onUploadProgress: (e: ProgressEvent<EventTarget>) => void
) => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    let formData = new FormData();
    formData.append("product", JSON.stringify(data.data.fields));

    for (var x = 0; x < data.data.images.length; x++) {
      formData.append("file", data.data.images[x]);
    }
    for (var x = 0; x < data.data.internal_bodies_images.length; x++) {
      formData.append("internal_bodies_images", data.data.internal_bodies_images[x]);
    }
    let token = userStorage.getUser();
    const response = await axios.put(`${API_URL}/admin/multisplit/${data.id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно променихте продукта.",
          hasError: false,
        })
      );
      dispatch(fetchProductsList("multisplit"));
    });
  } catch (error: any) {
    dispatch(setResponseMessage({ message: error.message, hasError: true }));

    //  console.log(`Error in addProduct: ${error.message}`);
  }
};

export const deleteProduct = (id: string, type: string = "product") => async (dispatch: any) => {
  try {
    let token = userStorage.getUser();
    const response = await axios.delete(`${API_URL}/admin/${type === "product" ? "products" : type}/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно изтрихте продукта.",
          hasError: false,
        })
      );
      dispatch(fetchProductsList(type));
    });
  } catch (error: any) {
    dispatch(setResponseMessage({ message: error.message, hasError: true }));

    //  console.log(`Error in deleteProduct: ${error.message}`);
  }
};

export const fetchProduct = (id: string, type: string = "product") => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));

    let token = userStorage.getUser();
    const result = await axios.get(`${API_URL}/admin/${type}-values/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    dispatch(setProductForEdit(result.data.data));
  } catch (error: any) {
    batch(() => {
      dispatch(setLoading(false));
      dispatch(setResponseMessage({ message: error.message, hasError: true }));
    });

    //  console.log(`Error in editProduct: ${error.message}`);
  }
};
export default productsAdmin.reducer;
